import { useEffect, useState } from 'react';

// True while the audio track carries speech-level sound (drives the teal active-speaker border).
// Uses one shared AudioContext and an AnalyserNode per track; fails silently where WebAudio is
// unavailable (jsdom, old browsers, iOS before a user gesture).
let sharedContext = null;
const getContext = () => {
  if (sharedContext) return sharedContext;
  const Ctx = typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : null;
  if (!Ctx) return null;
  try {
    sharedContext = new Ctx();
  } catch {
    sharedContext = null;
  }
  return sharedContext;
};

export const useSpeaking = (track, { enabled = true, threshold = 0.035, holdMs = 700 } = {}) => {
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    const mediaStreamTrack = track?.mediaStreamTrack;
    if (!enabled || !mediaStreamTrack || track.isEnabled === false) {
      setSpeaking(false);
      return undefined;
    }
    const ctx = getContext();
    if (!ctx || typeof MediaStream === 'undefined') return undefined;

    let source;
    let analyser;
    let timer;
    let lastLoud = 0;
    try {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
    } catch {
      return undefined;
    }
    const data = new Float32Array(analyser.fftSize);
    timer = setInterval(() => {
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      const now = Date.now();
      if (rms > threshold) lastLoud = now;
      const next = now - lastLoud < holdMs;
      setSpeaking((prev) => (prev === next ? prev : next));
    }, 150);

    return () => {
      clearInterval(timer);
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        /* already disconnected */
      }
    };
  }, [track, track?.isEnabled, enabled, threshold, holdMs]);

  return speaking;
};

export default useSpeaking;
